// ./src/utils/api/RentalMediaService.ts
//
// Rental-media service — condition evidence (photos / documents) attached to a
// rental, tagged phase = received | returned. The blob is uploaded to ms_storage
// first (via UploadedFileService, with the rental's context path); a row here then
// links that file id + kind/phase. OpResult-native. See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// Enum-like constants (mirror the eventsmagic-api boundary/rental.ts)
// =============================================================================

export const RENTAL_MEDIA_KIND = {
  PHOTO: "photo",
  DOCUMENT: "document",
} as const;

export const RENTAL_MEDIA_KIND_VALUES = Object.values(RENTAL_MEDIA_KIND);

export const RENTAL_MEDIA_PHASE = {
  RECEIVED: "received",
  RETURNED: "returned",
} as const;

export const RENTAL_MEDIA_PHASE_VALUES = Object.values(RENTAL_MEDIA_PHASE);

export const RENTAL_MEDIA_PHASE_LABELS: Record<string, string> = {
  received: "At pickup",
  returned: "At return",
};

// =============================================================================
// GraphQL
// =============================================================================

const RENTAL_MEDIA_FIELDS = `
  id
  eventRentalId
  uploadedFileId
  kind
  phase
  caption
  orderNo
  status
  rights
  file { id kind name originalFilename mimeType sizeBytes fileUrl thumbnailUrl }
`;

const RENTAL_MEDIA_LIST_QUERY = gql`
  query RentalMediaList($filter: RentalMediaFilter, $params: PaginationAndSorting) {
    rentalMediaList(filter: $filter, params: $params) {
      code
      data { ${RENTAL_MEDIA_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const RENTAL_MEDIA_CREATE_MUTATION = gql`
  mutation RentalMediaCreate($params: RentalMediaInput) {
    rentalMediaCreate(params: $params) {
      code
      data { ${RENTAL_MEDIA_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const RENTAL_MEDIA_UPDATE_MUTATION = gql`
  mutation RentalMediaUpdate($where: RentalMediaWhereInput, $params: RentalMediaInput) {
    rentalMediaUpdate(where: $where, params: $params) {
      code
      data { ${RENTAL_MEDIA_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const RENTAL_MEDIA_REMOVE_MUTATION = gql`
  mutation RentalMediaRemove($where: RentalMediaWhereInput) {
    rentalMediaRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

export interface RentalMediaInput {
  eventRentalId?: string | null;
  uploadedFileId?: string | null;
  kind?: string | null;
  phase?: string | null;
  caption?: string | null;
  orderNo?: number | null;
  status?: number | null;
}

export interface RentalMediaFilter {
  id?: string[] | null;
  eventRentalId?: string[] | null;
  kind?: string[] | null;
  phase?: string[] | null;
  status?: number[] | null;
}

export interface RentalMediaWhereInput {
  id?: string | string[];
}

// =============================================================================
// Service
// =============================================================================

export class RentalMediaService {
  static async list(
    filter?: RentalMediaFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(RENTAL_MEDIA_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async create(params: RentalMediaInput): Promise<OpResult> {
    return ApiService.executeMutation(RENTAL_MEDIA_CREATE_MUTATION, { params });
  }

  static async update(id: string, params: RentalMediaInput): Promise<OpResult> {
    return ApiService.executeMutation(RENTAL_MEDIA_UPDATE_MUTATION, { where: { id }, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(RENTAL_MEDIA_REMOVE_MUTATION, { where: { id } });
  }
}
